<section [formGroup]="group()" class="grid gap-8">
  <!-- The 4P grid: the sides are the column headings (shown once), the factors are the rows. At least one cell must be written. -->
  <div>
    <h2 class="mb-1 text-lg font-semibold text-slate-900">4P factor model</h2>
    <p class="mb-3 text-sm text-slate-500">
      The 4P factors across the biological, psychological and social or economic sides. Write at least one.
      @if (group().controls.fourPFactors.hasError('atLeastOneNote') && !readOnly()) {
        <span class="text-red-600">Nothing has been written yet.</span>
      }
    </p>
    <div formArrayName="fourPFactors" class="grid gap-x-3 gap-y-1 md:grid-cols-[8rem_1fr_1fr_1fr]">
      <div class="hidden md:contents" aria-hidden="true">
        <span></span>
        @for (dimension of dimensionTypes(); track dimension.id) {
          <span class="px-1 text-sm font-medium text-slate-600">{{ dimension.name }}</span>
        }
      </div>
      @for (factor of factorTypes(); track factor.id; let factorIndex = $index) {
        <div class="flex items-start gap-1 pt-3 md:pt-4">
          <h3 class="font-medium text-slate-800">{{ factor.name }}</h3>
          <mat-icon class="cursor-help text-slate-400" [matTooltip]="factorHint(factor.code)">info</mat-icon>
        </div>
        @for (dimension of dimensionTypes(); track dimension.id; let dimensionIndex = $index) {
          <div [formGroupName]="factorIndex * dimensionTypes().length + dimensionIndex">
            <span class="text-xs text-slate-500 md:hidden">{{ dimension.name }}</span>
            <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
              <textarea
                matInput
                rows="2"
                formControlName="notes"
                [attr.aria-label]="factor.name + ', ' + dimension.name"></textarea>
            </mat-form-field>
          </div>
        }
      }
    </div>
  </div>

  <!-- The life stages, one collapsible row each; a ticked row has something written in it -->
  <div>
    <h2 class="mb-1 text-lg font-semibold text-slate-900">Development stage</h2>
    <p class="mb-3 text-sm text-slate-500">Significant experiences in each stage of the member's life. Open a stage to write in it.</p>
    <mat-accordion multi formArrayName="developmentStages">
      @for (row of stageRows().controls; track $index; let index = $index) {
        <mat-expansion-panel [formGroupName]="index">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ lookupName('developmentStages', row.value.developmentStageId) }}</mat-panel-title>
            <mat-panel-description>
              {{ lookupDescription('developmentStages', row.value.developmentStageId) }}
              @if (stageFilled(row)) {
                <mat-icon class="!ml-2 text-emerald-600" aria-label="Filled in">check_circle</mat-icon>
              }
            </mat-panel-description>
          </mat-expansion-panel-header>
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>{{ lookupName('developmentStages', row.value.developmentStageId) }}</mat-label>
            <textarea matInput rows="3" formControlName="notes"></textarea>
          </mat-form-field>
        </mat-expansion-panel>
      }
    </mat-accordion>
  </div>

  <!-- The four patterns, each rated (required) with an optional note that opens on request -->
  <div>
    <h2 class="mb-1 text-lg font-semibold text-slate-900">Noticeable patterns</h2>
    <p class="mb-4 text-sm text-slate-500">
      Patterns related to the current problem, and how much influence each has over it. Every pattern is rated; "Not applicable" is a rating.
    </p>
    <div formArrayName="patternRatings" class="grid gap-x-8 gap-y-5 md:grid-cols-2">
      @for (row of patternRows().controls; track $index; let index = $index) {
        <div [formGroupName]="index" class="grid content-start gap-1">
          <scale-slider
            [label]="lookupName('patternTypes', row.value.patternTypeId)"
            formControlName="influenceRating"
            [options]="options('severityScales')"
            [numeric]="true"
            [required]="true" />
          @if (noteOpen(index, row)) {
            <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
              <mat-label>{{ lookupName('patternTypes', row.value.patternTypeId) }}: what the member describes</mat-label>
              <textarea matInput rows="2" formControlName="notes"></textarea>
            </mat-form-field>
          } @else if (!readOnly()) {
            <div>
              <button mat-button type="button" class="!px-1 !text-sm" (click)="openNote(index)">
                <mat-icon>edit_note</mat-icon>
                Add a note
              </button>
            </div>
          }
        </div>
      }
    </div>
  </div>
</section>
